import { ClipboardService } from 'ngx-clipboard'
import { FormControl } from '@angular/forms'
import { AddChargeComponent } from '../add-charge/add-charge.component'
import { Booking } from '@app/domain/Booking'
import { BookingChangedMetadata } from '@app/features/shared/components/booking/booking.component'
import { BookingService } from '@services/booking.service'
import { BookingStatusType } from '@app/domain/BookingStatus'
import { Business } from '@app/domain/Business'
import {
    Component,
    EventEmitter,
    Input, OnChanges, OnDestroy,
    Output, SimpleChanges,
    TemplateRef,
    ViewChild,
} from '@angular/core'
import { ContextService } from '@services/context.service'
import { DateTime } from 'luxon'
import { DepositPayment } from '@app/domain/DepositPayment'
import { DiaryPreferencesService } from '@app/features/bookings/services/diary-preferences.service'
import { DurationUnit } from '../../pipes/duration.pipe'
import { ModalComponent } from '@app/features/shared/components/modal/modal.component'
import { NgbModal } from '@ng-bootstrap/ng-bootstrap'
import { ToastBuilder } from '@app/domain/ToastBuilder'
import { ToastService } from '@services/toast.service'
import { ToastType } from '@app/domain/Toast'
import { chargeFailureMessage, paymentProcessingMessage } from '@app/domain/PaymentDeclineMessage'
import { Venue } from '@app/domain/Venue'
import { EMPTY, catchError, exhaustMap, filter, finalize, map, mergeMap, startWith, takeUntil, tap } from 'rxjs'

@Component({
    selector: 'app-booking-payments',
    templateUrl: './booking-payments.component.html',
    styleUrls: ['./booking-payments.component.sass'],
})
export class BookingPaymentsComponent implements OnChanges, OnDestroy {
    @Input() business!: Business
    @Input() venue!: Venue
    @Input() booking!: Booking
    @Output() bookingChanged = new EventEmitter<[Booking, Partial<BookingChangedMetadata>]>()
    @ViewChild('refundProductsModal') refundProductsModal!: TemplateRef<any>
    selectedOrderLineIds = new Set<string>()
    orderRefundError: string | null = null
    isRefundingOrder = false
    private orderRefundModal: ModalComponent | null = null
    @ViewChild('waivePendingPaymentModal') waivePendingPaymentModal!: TemplateRef<any>
    @ViewChild('confirmRefundPaymentModal') confirmRefundPaymentModal!: TemplateRef<any>
    @ViewChild('confirmRefundCancellationModal') confirmRefundCancellationModal!: TemplateRef<any>
    @ViewChild('confirmPartialChargeModal') confirmPartialChargeModal!: TemplateRef<any>
    @ViewChild('confirmRemoveCancellationModal') confirmRemoveCancellationModal!: TemplateRef<any>
    noPaymentsInvolved = false
    isEditingBooking = false
    canRequireCardDetails = false
    isRefundingPayment = false
    isRefundingCancellation = false
    chargeFailureMessage: string | null = null
    removalFailureMessage: string | null = null
    isRemovingCancellation = false
    paymentToRefund: DepositPayment | null = null
    refundPaymentAmount: number | null = null
    refundPaymentError: string | null = null
    paymentSummary!: ReturnType<Booking['paymentSummary']>
    refundableDepositPayments: DepositPayment[] = []
    pendingPaymentDeliveryMethod = new FormControl<'email' | 'sms' | null>(null)
    selectedCoversToCharge: number = 0
    partialChargeCovers: number = 1
    private onDestroy$ = new EventEmitter<void>()

    coversPluralMapping: { [key: string]: string } = {
        '=1': '1 cover',
        'other': '# covers',
    }

    constructor(
        private contextService: ContextService,
        protected diaryPreferencesService: DiaryPreferencesService,
        private bookingService: BookingService,
        private toastService: ToastService,
        private modalService: NgbModal,
        private clipboardService: ClipboardService
    ) { }

    ngOnChanges(changes: SimpleChanges) {
        this.canRequireCardDetails = this.business.paymentsAccountForCapability('CancellationChargePayments') !== null
        if (changes['booking']) {
            this.noPaymentsInvolved = this.booking.pendingCancellationCharge === null
            && this.booking.pendingDepositCharge === null
            && this.booking.cancellation === null
            && this.booking.deposit === null
            this.selectedCoversToCharge = this.remainingCoversToCharge()
        }
        this.paymentSummary = this.booking.paymentSummary()
        this.refundableDepositPayments = (this.booking.deposit?.payments ?? []).filter(payment => payment.amountRefundable() > 0)
    }

    ngOnDestroy() {
        this.onDestroy$.emit()
        this.onDestroy$.complete()
    }

    get orderRefundTotal(): number {
        return this.booking.sale?.selectedRefundAmount(this.selectedOrderLineIds) ?? 0
    }

    toggleOrderLine(lineId: string) {
        if (this.isRefundingOrder) {
            return
        }
        if (this.selectedOrderLineIds.has(lineId)) {
            this.selectedOrderLineIds.delete(lineId)
        } else {
            this.selectedOrderLineIds.add(lineId)
        }
        if (this.orderRefundModal) {
            this.orderRefundModal.isActionDisabled = this.orderRefundTotal <= 0
        }
    }

    refundProducts() {
        const area = this.venue.areaBookingIsIn(this.booking)
        if (!area) {
            return
        }
        if (!this.booking.sale?.payment?.datePaid || this.booking.sale.payment.balance <= 0 || this.isRefundingOrder) {
            return
        }
        this.selectedOrderLineIds = new Set()
        this.orderRefundError = null
        const modal = this.modalService.open(ModalComponent, {
            backdrop: 'static', keyboard: false, beforeDismiss: () => !this.isRefundingOrder,
        })
        const component = modal.componentInstance as ModalComponent
        this.orderRefundModal = component
        component.title = this.booking.sale.payment.canRefundOnline ? 'Refund Products' : 'Mark Products as Refunded'
        component.actionType = 'confirm'
        component.actionButtonTitle = this.booking.sale.payment.canRefundOnline ? 'Refund Selected Products' : 'Mark Selected Products as Refunded'
        component.cancelButtonTitle = 'Go Back'
        component.contentTemplate = this.refundProductsModal
        component.isActionDisabled = true
        component.actionSelected.pipe(
            filter(() => this.orderRefundTotal > 0),
            exhaustMap(() => {
                this.isRefundingOrder = true
                component.isPerformingAction = true
                component.isActionDisabled = true
                this.orderRefundError = null
                return this.bookingService.refundOrderLines(this.business.id, this.venue.id,
                    area.id, this.booking.id, [...this.selectedOrderLineIds]).pipe(
                    finalize(() => {
                        this.isRefundingOrder = false
                        component.isPerformingAction = false
                        component.isActionDisabled = this.orderRefundTotal <= 0
                    }),
                    catchError(() => {
                        this.orderRefundError = 'Could not confirm the product refund. Refresh the booking to check its payment history.'
                        return EMPTY
                    })
                )
            }),
            takeUntil(modal.hidden),
            takeUntil(this.onDestroy$)
        ).subscribe(booking => {
            modal.close()
            this.showToast(ToastType.Success, 'Products refunded', 'Payment Updated')
            this.bookingChanged.emit([booking, { changeHasFinishedInteraction: false }])
        })
    }

    addCancellationCharge() {
        this.addCharge('cancellationCharge')
    }

    addDepositCharge() {
        this.addCharge('depositCharge')
    }

    removeCancellation() {
        const cancellationId = this.booking.cancellation?.id
        if (!cancellationId || !this.booking.canRemoveCancellation() || this.isRemovingCancellation) {
            return
        }
        this.removalFailureMessage = null
        const modal = this.modalService.open(ModalComponent, {
            backdrop: 'static',
            keyboard: false,
            beforeDismiss: () => !this.isRemovingCancellation,
        })
        const component = modal.componentInstance as ModalComponent
        component.title = 'Remove Cancellation'
        component.actionType = 'confirm'
        component.actionButtonTitle = 'Remove Cancellation'
        component.cancelButtonTitle = 'Cancel'
        component.contentTemplate = this.confirmRemoveCancellationModal
        component.actionSelected.pipe(
            exhaustMap(() => {
                this.isRemovingCancellation = true
                component.isPerformingAction = true
                component.isActionDisabled = true
                this.removalFailureMessage = null
                return this.bookingService.removeCancellation(
                    this.booking.organisationId, this.business.id, this.venue.id, this.booking.id, cancellationId
                ).pipe(
                    finalize(() => {
                        this.isRemovingCancellation = false
                        component.isPerformingAction = false
                        component.isActionDisabled = false
                    }),
                    catchError(error => {
                        this.removalFailureMessage = error.status === 409
                            ? 'This cancellation can no longer be removed. Refresh the booking and try again.'
                            : 'Couldn\'t remove the cancellation. Please try again.'
                        return EMPTY
                    })
                )
            }),
            takeUntil(modal.hidden),
            takeUntil(this.onDestroy$)
        ).subscribe(booking => {
            modal.close()
            this.showToast(ToastType.Success, 'Cancellation removed', 'Booking Updated')
            this.bookingChanged.emit([booking, { changeHasFinishedInteraction: false }])
        })
    }

    editPendingPayment() {
        const modal = this.modalService.open(AddChargeComponent)
        const component = modal.componentInstance as AddChargeComponent
        component.business = this.business
        component.venue = this.venue
        component.booking = this.booking
        component.bookingChanged
            .pipe(
                takeUntil(this.onDestroy$),
                tap(() => modal.close())
            )
            .subscribe({
                next: (booking) => {
                    this.toastService.show(
                        new ToastBuilder('Charge Updated')
                            .withType(ToastType.Success)
                            .withHeader('Edit Charge')
                            .build()
                    )
                    this.bookingChanged.emit([booking, { changeHasFinishedInteraction: false }])
                },
                error: _ => {
                    //TODO Track the error
                    this.showToast(
                        ToastType.Danger,
                        'Couldn\'t edit pending payment. Please try again.',
                        'Booking Update Error'
                    )
                },
            })
    }

    waivePendingPayment() {
        const modal = this.modalService.open(ModalComponent)
        const component = modal.componentInstance as ModalComponent
        component.title = 'Confirm'
        component.actionType = 'confirm'
        component.actionButtonTitle = 'Waive'
        component.cancelButtonTitle = 'Cancel'
        component.contentTemplate = this.waivePendingPaymentModal
        component.actionSelected
            .pipe(takeUntil(this.onDestroy$))
            .subscribe({
                next: () => {
                    modal.close()
                    this.unseatBooking()
                },
            })
    }

    markAsManuallyPaid() {
        const area = this.venue.areaBookingIsIn(this.booking)
        if (!area) {
            return
        }
        this.isEditingBooking = true
        this.bookingService.markDepositAsManuallyPaid(
            this.business.id,
            this.venue.id,
            area.id,
            this.booking.id
        )
            .pipe(
                takeUntil(this.onDestroy$),
                finalize(() => this.isEditingBooking = false)
            )
            .subscribe({
                next: booking => {
                    this.showToast(
                        ToastType.Success,
                        'Deposit marked as paid',
                        'Payment Confirmed'
                    )
                    this.bookingChanged.emit([booking, { changeHasFinishedInteraction: false }])
                },
                error: _ => {
                    this.showToast(
                        ToastType.Danger,
                        'Couldn\'t mark deposit as paid. Please try again.',
                        'Booking Update Error'
                    )
                },
            })
    }

    remainingCoversToCharge(): number {
        if (!this.booking.cancellation) {
            return this.booking.size
        }
        return this.booking.cancellation.remainingCoversToCharge(this.booking.size)
    }

    canPartiallyChargeCoversForBookedBooking(): boolean {
        const eligibleStatuses = [BookingStatusType.Booked, BookingStatusType.Waiting, BookingStatusType.PartiallySeated, BookingStatusType.Seated]
        if (!eligibleStatuses.includes(this.booking.statusType)) {
            return false
        }
        if (!this.booking.cancellation) {
            return false
        }
        if (this.cancellationUsesDojo()) {
            return false
        }
        if (!this.booking.cancellation.paymentMethodId) {
            return false
        }
        if (!this.booking.cancellation.isPerCover) {
            return false
        }
        // Need at least 2 remaining covers to do a partial charge (1 to charge, 1 to keep)
        return this.remainingCoversToCharge() >= 2
    }

    canChargeFullAmountForCancelledBooking(): boolean {
        const cancelledStatuses = [BookingStatusType.Cancelled, BookingStatusType.NoShow]
        if (!cancelledStatuses.includes(this.booking.statusType)) {
            return false
        }
        if (this.cancellationUsesDojo()
            && (this.booking.cancellation?.charges.length ?? 0) > 0) {
            return false
        }
        return this.booking.canChargeCancellationFee()
    }

    cancellationUsesDojo(): boolean {
        const paymentsAccountId = this.booking.cancellation?.paymentsAccountId
        if (!paymentsAccountId) {
            return false
        }
        return this.business.paymentsAccounts
            .find(account => account.id === paymentsAccountId)
            ?.usesDojo() === true
    }

    maxPartialChargeCovers(): number {
        // Can charge up to remainingCovers - 1 (must leave at least 1 cover uncharged)
        return Math.max(1, this.remainingCoversToCharge() - 1)
    }

    getPartialChargeAmount(): number {
        if (!this.booking.cancellation) {
            return 0
        }
        return this.booking.cancellation.charge().amountForPartySize(this.partialChargeCovers)
    }

    isPerCoverCharge(): boolean {
        return this.booking.cancellation?.isPerCover ?? false
    }

    getChargeAmountForCovers(covers: number): number {
        if (!this.booking.cancellation) {
            return 0
        }
        return this.booking.cancellation.charge().amountForPartySize(covers)
    }

    onCoversToChargeChange(value: number) {
        this.selectedCoversToCharge = value
    }

    onPartialChargeCoversChange(value: number) {
        this.partialChargeCovers = value
    }

    chargePartialClicked() {
        const area = this.venue.areaBookingIsIn(this.booking)
        if (!area) {
            return
        }
        this.partialChargeCovers = 1
        const modal = this.modalService.open(ModalComponent)
        const component = modal.componentInstance as ModalComponent
        component.title = 'Select Covers to Charge'
        component.actionType = 'confirm'
        component.contentTemplate = this.confirmPartialChargeModal
        component.actionButtonTitle = 'Charge for Partial No-Shows'
        component.cancelButtonTitle = 'Cancel'
        component.cancelPerformsAction = false
        this.chargeFailureMessage = null
        component.actionSelected
            .pipe(
                takeUntil(this.onDestroy$),
                exhaustMap(() => {
                    component.isPerformingAction = true
                    this.chargeFailureMessage = null
                    return this.bookingService.chargeCancellationFee(
                        this.business.id,
                        this.venue.id,
                        area.id,
                        this.booking.id,
                        null,
                        this.partialChargeCovers
                    )
                        .pipe(
                            finalize(() => component.isPerformingAction = false),
                            catchError(error => {
                                this.chargeFailureMessage = this.getChargeFailureMessage(error)
                                return EMPTY
                            })
                        )
                })
            )
            .subscribe({
                next: booking => {
                    modal.close()
                    this.toastService.show(
                        new ToastBuilder('Partial charge applied successfully')
                            .withType(ToastType.Success)
                            .withHeader('Charge Complete')
                            .build()
                    )
                    this.bookingChanged.emit([booking, { changeHasFinishedInteraction: false }])
                },
            })
    }

    chargeCancellationFeeClicked(confirmContentTemplate: TemplateRef<any>) {
        const user = this.contextService.getUser()
        if (!user) {
            return
        }
        const area  = this.venue.areaBookingIsIn(this.booking)
        if (!area) {
            return
        }
        this.selectedCoversToCharge = this.remainingCoversToCharge()
        const modal = this.modalService.open(ModalComponent)
        const component = modal.componentInstance as ModalComponent
        component.title = 'Charge Cancellation Fee'
        component.actionType = 'confirm'
        component.contentTemplate = confirmContentTemplate
        component.actionButtonTitle = 'Charge'
        component.cancelButtonTitle = 'Cancel'
        component.cancelPerformsAction = false
        this.chargeFailureMessage = null
        component.actionSelected
            .pipe(
                takeUntil(this.onDestroy$),
                exhaustMap(() => {
                    component.isPerformingAction = true
                    this.chargeFailureMessage = null
                    const coversToCharge = this.isPerCoverCharge() && !this.cancellationUsesDojo()
                        ? this.selectedCoversToCharge
                        : null
                    return this.bookingService.chargeCancellationFee(
                        this.business.id,
                        this.venue.id,
                        area.id,
                        this.booking.id,
                        null,
                        coversToCharge
                    )
                        .pipe(
                            finalize(() => component.isPerformingAction = false),
                            catchError(error => {
                                this.chargeFailureMessage = this.getChargeFailureMessage(error)
                                return EMPTY
                            })
                        )
                })
            )
            .subscribe({
                next: booking => {
                    modal.close()
                    this.bookingChanged.emit([booking, { changeHasFinishedInteraction: false }])
                },
            })
    }

    refundPaymentAmountInMinorUnits(): number | null {
        const amount = Number(this.refundPaymentAmount)
        if (!Number.isFinite(amount)) {
            return null
        }
        return Math.round(amount * 100)
    }

    isRefundPaymentAmountValid(): boolean {
        const amount = this.refundPaymentAmountInMinorUnits()
        return this.paymentToRefund !== null
            && amount !== null
            && amount > 0
            && amount <= this.paymentToRefund.amountRefundable()
    }

    canAddAnotherDepositCharge(): boolean {
        return this.booking.deposit !== null
            && this.booking.pendingDepositCharge === null
            && this.booking.pendingCancellationCharge === null
    }

    copyPendingPaymentLinkToClipboard() {
        this.clipboardService.copy(this.bookingService.getBookingDetailsUrl(this.booking))
        this.showToast(ToastType.Success, 'Payment link copied to clipboard', 'Copied')
    }

    resendPendingPaymentRequested(modalContent: TemplateRef<any>) {
        const organisation = this.contextService.getOrganisation()
        if (!organisation) {
            return
        }
        const modal = this.modalService.open(ModalComponent)
        const component = modal.componentInstance as ModalComponent
        component.title = this.booking.canPayForOrder() ? 'Send Product Payment Link' : 'Resend Pending Payment Link'
        component.contentTemplate = modalContent
        component.actionType = 'confirm'
        component.actionButtonTitle = 'Send Link'
        this.pendingPaymentDeliveryMethod.setValue(this.booking.emailAddress ? 'email' : this.booking.phoneNumber ? 'sms' : null)
        component.actionValidation = this.pendingPaymentDeliveryMethod.valueChanges.pipe(
            startWith(this.pendingPaymentDeliveryMethod.value), map(value => value !== null)
        )
        component.actionSelected.pipe(
            filter(() => this.pendingPaymentDeliveryMethod.value !== null),
            exhaustMap(() => {
                component.isPerformingAction = true
                return this.bookingService.bookingPendingPaymentResendRequested(
                    organisation, this.business, this.venue, this.booking,
                    this.pendingPaymentDeliveryMethod.value === 'email',
                    this.pendingPaymentDeliveryMethod.value === 'sms'
                ).pipe(
                    finalize(() => component.isPerformingAction = false),
                    catchError(() => {
                        this.showToast(ToastType.Danger, 'Could not send the payment link. Please try again.', 'Error')
                        return EMPTY
                    })
                )
            }),
            takeUntil(modal.hidden),
            takeUntil(this.onDestroy$)
        ).subscribe(() => {
            modal.close()
            this.showToast(ToastType.Success, 'Payment link sent', 'Success')
        })
    }

    refundPaymentClicked(payment: DepositPayment) {
        const area = this.venue.areaBookingIsIn(this.booking)
        const refundableAmount = payment.amountRefundable()
        if (!area || refundableAmount === 0) {
            return
        }
        const modal = this.modalService.open(ModalComponent)
        const component = modal.componentInstance as ModalComponent
        const isOnlinePayment = payment.paymentIntentId !== null
        this.paymentToRefund = payment
        this.refundPaymentAmount = refundableAmount / 100
        this.refundPaymentError = null
        component.title = isOnlinePayment ? 'Refund Payment' : 'Mark Payment as Refunded'
        component.actionType = 'confirm'
        component.actionButtonTitle = isOnlinePayment ? 'Refund' : 'Mark as Refunded'
        component.cancelButtonTitle = 'Cancel'
        component.contentTemplate = this.confirmRefundPaymentModal
        component.actionSelected
            .pipe(
                takeUntil(this.onDestroy$),
                tap(() => {
                    this.refundPaymentError = this.isRefundPaymentAmountValid()
                        ? null
                        : 'Enter an amount greater than zero and no more than the refundable payment.'
                }),
                filter(() => this.isRefundPaymentAmountValid()),
                tap(() => {
                    component.isPerformingAction = true
                    this.isRefundingPayment = true
                }),
                mergeMap(() => {
                    return this.bookingService.refundDepositPayment(
                        this.business.id,
                        this.venue.id,
                        area.id,
                        this.booking.id,
                        payment.id,
                        this.refundPaymentAmountInMinorUnits()!
                    ).pipe(
                        finalize(() => {
                            component.isPerformingAction = false
                            this.isRefundingPayment = false
                        })
                    )
                })
            )
            .subscribe({
                next: (booking) => {
                    modal.close()
                    this.toastService.show(
                        new ToastBuilder(isOnlinePayment ? 'Payment Refunded' : 'Payment Marked as Refunded')
                            .withType(ToastType.Success)
                            .withHeader(isOnlinePayment ? 'Refund Successful' : 'Payment Updated')
                            .build()
                    )
                    this.bookingChanged.emit([booking, { changeHasFinishedInteraction: false }])
                },
                error: error => {
                    this.showToast(
                        ToastType.Danger,
                        paymentProcessingMessage(error) ?? (isOnlinePayment
                            ? 'Couldn\'t refund payment. Please try again.'
                            : 'Couldn\'t mark payment as refunded. Please try again.'),
                        'Refund Error'
                    )
                },
            })
    }

    canRefundCancellationCharge(): boolean {
        if (!this.booking.cancellation) {
            return false
        }
        if (this.booking.cancellation.charges.length === 0) {
            return false
        }
        const charge = this.booking.cancellation.charges[0]
        return charge.dateRefunded === null
    }

    refundCancellationChargeClicked() {
        const area = this.venue.areaBookingIsIn(this.booking)
        if (!area) {
            return
        }
        const modal = this.modalService.open(ModalComponent)
        const component = modal.componentInstance as ModalComponent
        component.title = 'Refund Cancellation Charge'
        component.actionType = 'confirm'
        component.actionButtonTitle = 'Refund'
        component.cancelButtonTitle = 'Cancel'
        component.contentTemplate = this.confirmRefundCancellationModal
        component.actionSelected
            .pipe(
                takeUntil(this.onDestroy$),
                tap(() => {
                    component.isPerformingAction = true
                    this.isRefundingCancellation = true
                }),
                finalize(() => {
                    component.isPerformingAction = false
                    this.isRefundingCancellation = false
                }),
                mergeMap(() => {
                    return this.bookingService.refundCancellationCharge(
                        this.business.id,
                        this.venue.id,
                        area.id,
                        this.booking.id
                    )
                })
            )
            .subscribe({
                next: (booking) => {
                    modal.close()
                    this.toastService.show(
                        new ToastBuilder('Cancellation Charge Refunded')
                            .withType(ToastType.Success)
                            .withHeader('Refund Successful')
                            .build()
                    )
                    this.bookingChanged.emit([booking, { changeHasFinishedInteraction: false }])
                },
                error: error => {
                    this.showToast(
                        ToastType.Danger,
                        paymentProcessingMessage(error) ?? 'Couldn\'t refund cancellation charge. Please try again.',
                        'Refund Error'
                    )
                },
            })
    }

    private unseatBooking() {
        const area = this.venue.areaBookingIsIn(this.booking)
        if (!area) {
            return
        }
        this.isEditingBooking = true
        this.bookingService.unseatBooking(
            this.business.id,
            this.venue.id,
            area.id,
            this.booking.id
        )
            .pipe(
                takeUntil(this.onDestroy$),
                finalize(() => this.isEditingBooking = false)
            )
            .subscribe(booking => {
                this.bookingChanged.emit([booking, {}])
            })
    }

    private addCharge(chargeType: 'cancellationCharge' | 'depositCharge') {
        const modal = this.modalService.open(AddChargeComponent)
        const component = modal.componentInstance as AddChargeComponent
        component.chargeType = chargeType
        component.business = this.business
        component.venue = this.venue
        component.booking = this.booking
        const isRepeatDepositCharge = chargeType === 'depositCharge' && (this.booking.deposit?.amountHeld() ?? 0) > 0
        component.amountOnly = isRepeatDepositCharge
        component.forceNoExpiry = isRepeatDepositCharge
        component.bookingChanged
            .pipe(
                takeUntil(this.onDestroy$),
                tap(() => modal.close())
            )
            .subscribe({
                next: (booking) => {
                    this.bookingChanged.emit([booking, { changeHasFinishedInteraction: false }])
                },
                error: _ => {
                    //TODO Track the error
                    this.showToast(
                        ToastType.Danger,
                        'Couldn\'t add cancellation charge. Please try again.',
                        'Booking Update Error'
                    )
                },
            })
    }

    private showToast(toastType: ToastType, message: string, title?: string) {
        let  toastBuilder = new ToastBuilder(message)
            .withType(toastType)
        if (title){
            toastBuilder = toastBuilder.withHeader(title)
        }
        this.toastService.show(toastBuilder.build())
    }

    private getChargeFailureMessage(error: unknown): string {
        return chargeFailureMessage(error)
    }

    protected readonly BookingStatusType = BookingStatusType
    protected readonly DurationUnit = DurationUnit
    protected readonly DateTime = DateTime
}
